import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { apiFetch, ApiError } from '@/lib/api';
import { Card, CardLabel } from '@/components/ui/card';
import { StatCard } from '@/components/StatCard';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/DataTable';

interface EmptyPostRow {
  _id: string;
  postType: string;
  reason: string;
  ownerId: string | null;
  ownerUsername: string | null;
  caption: string | null;
  status: string | null;
  reviewStatus: string | null;
  createdAt: string | null;
}

interface EmptyMediaResponse {
  count: number;
  posts: EmptyPostRow[];
}

interface DeleteResult {
  ok: true;
  deletedCount: number;
  deletedIds: string[];
}

function dateCell(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function typeBadge(postType: string): JSX.Element {
  const color = postType === 'clip' ? 'text-brand-cyan' : 'text-fg-muted';
  return <span className={`text-xs font-bold uppercase ${color}`}>{postType}</span>;
}

function reasonBadge(reason: string): JSX.Element {
  const color =
    reason === 'missing video'
      ? 'bg-state-warning/15 text-state-warning'
      : 'bg-state-danger/15 text-state-danger';
  return (
    <span className={`inline-block rounded-md px-2 py-0.5 text-xs font-medium ${color}`}>
      {reason}
    </span>
  );
}

const columns: Column<EmptyPostRow>[] = [
  {
    key: 'postType',
    header: 'Type',
    width: '80px',
    cell: (r) => typeBadge(r.postType),
  },
  {
    key: 'reason',
    header: 'Issue',
    width: '120px',
    cell: (r) => reasonBadge(r.reason),
  },
  {
    key: 'ownerUsername',
    header: 'Owner',
    cell: (r) => r.ownerUsername ?? <span className="text-fg-subtle">unknown</span>,
  },
  {
    key: 'caption',
    header: 'Caption',
    cell: (r) =>
      r.caption ? (
        <span className="line-clamp-1">{r.caption}</span>
      ) : (
        <span className="text-fg-subtle">--</span>
      ),
  },
  {
    key: 'status',
    header: 'Status',
    cell: (r) => (
      <span className="text-xs uppercase text-fg-subtle">
        {r.status ?? '--'}
      </span>
    ),
  },
  {
    key: 'createdAt',
    header: 'Created',
    cell: (r) => (
      <span className="text-xs tabular-nums text-fg-subtle">
        {dateCell(r.createdAt)}
      </span>
    ),
  },
  {
    key: '_id',
    header: 'ID',
    cell: (r) => (
      <code className="text-[11px] text-fg-subtle">{r._id.slice(-8)}</code>
    ),
  },
];

export function Posts(): JSX.Element {
  const qc = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emptyQuery = useQuery({
    queryKey: ['moderation', 'empty-media-posts'],
    queryFn: () =>
      apiFetch<EmptyMediaResponse>('/api/moderation/empty-media-posts'),
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiFetch<DeleteResult>('/api/moderation/empty-media-posts', {
        method: 'DELETE',
      }),
    onSuccess: (data) => {
      setConfirmDelete(false);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['moderation', 'empty-media-posts'] });
      void qc.invalidateQueries({ queryKey: ['metrics', 'content'] });
      void qc.invalidateQueries({ queryKey: ['metrics', 'overview'] });
      alert(`Deleted ${data.deletedCount} incomplete post${data.deletedCount === 1 ? '' : 's'}.`);
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.message);
      else setError('Delete failed.');
    },
  });

  const count = emptyQuery.data?.count ?? 0;
  const posts = emptyQuery.data?.posts;
  const hasEmpty = !emptyQuery.isLoading && count > 0;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="mb-1 text-2xl font-bold">Posts</h1>
        <p className="text-sm text-fg-muted">
          Post health and cleanup tools.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          label="Incomplete uploads"
          value={emptyQuery.data?.count}
          loading={emptyQuery.isLoading}
          error={emptyQuery.error ? (emptyQuery.error as Error).message : null}
          hint="posts with missing or incomplete media"
        />
      </div>

      <Card className="flex flex-col gap-4">
        <div>
          <CardLabel>Incomplete upload cleanup</CardLabel>
          <div className="text-lg font-bold mt-1">
            Posts with missing or incomplete media
          </div>
          <div className="text-xs text-fg-subtle mt-1">
            Photo/clip posts where the upload never fully completed — either no
            media at all, or clips where only the thumbnail landed and the video
            file is missing. These are invisible to users but waste space.
          </div>
        </div>

        {!hasEmpty && !emptyQuery.isLoading && (
          <div className="text-sm text-fg-muted">No incomplete posts found.</div>
        )}

        {hasEmpty && (
          <DataTable
            columns={columns}
            rows={posts}
            loading={emptyQuery.isLoading}
            empty="No incomplete posts found."
            rowKey={(r) => r._id}
          />
        )}

        {hasEmpty && !confirmDelete && (
          <div>
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                setConfirmDelete(true);
                setError(null);
              }}
            >
              <Trash2 size={14} className="text-state-danger" />
              Delete {count} incomplete {count === 1 ? 'post' : 'posts'}
            </Button>
          </div>
        )}

        {confirmDelete && (
          <div className="flex flex-col gap-3 rounded-md border border-state-danger/30 p-4">
            <div className="text-sm">
              This will permanently delete{' '}
              <span className="font-bold text-state-danger">{count}</span>{' '}
              {count === 1 ? 'post' : 'posts'} with incomplete uploads.
              This cannot be undone.
            </div>
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Confirm delete'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setConfirmDelete(false);
                  setError(null);
                }}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
            </div>
            {error && (
              <div className="text-xs text-state-danger">{error}</div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
